import { get } from 'lodash';
import { slugifyParam, slugifyArray, slugify, Url } from 'utils/url';
import routeMonitor from 'utils/routeMonitor';
import { config } from 'api';
import FilterType from 'apps/catalogue/models/FilterType';
import QueryBuilder from './QueryBuilder';

const deliveryPassUrl = '/webshop/displayDeliveryPass.do'; // TODO should be configurable (different for each retailer)
const promoLinkObject = {
  REGULAR: (id, text) => `/offers/${slugifyParam(text, id)}`,
  BUNDLE: (id, text, type) => `/webshop/${type.toLowerCase()}/${slugify(text)}/${id}`,
  COMBO: (id, text, type) => `/webshop/${type.toLowerCase()}/${slugify(text)}/${id}`,
  SMART_PASS: () => deliveryPassUrl,
  SMART_PASS_ELIGIBLE: (id) => `${deliveryPassUrl}${id ? `?schemeID=${id}` : ''}`,
};

export const catalogueStaticLinks = Object.freeze({
  OFFERS: '/on-offer',
  BROWSE: '/browse',
  YOUR_SAVINGS_HUB: '/your-savings-hub',
  NEW: '/browse?filters=new-19999',
  AISLES: '/navigation/browse',
  TOP_PICKS: `/navigation/${config.routes.navigation.highlighted}`,
});

function isBetaFeatureEnabled(betaFeatureName) {
  const store = routeMonitor && routeMonitor.store;
  const state = (store && store.getState) ? store.getState() : {};
  return get(state, 'user.betaFeatures', []).includes(betaFeatureName);
}

export function getQuery(pageParameters = {}, includeShowAllFavourites = false) {
  const {
    campaigns, searchTerm, sort, filters, customFilters, priceFilters, hiddenOOS, cluster, showAllFavourites,
  } = pageParameters;

  const queryBuilder = new QueryBuilder()
    .withCampaigns(campaigns)
    .withSearchTerm(searchTerm)
    .withSort(sort)
    .withFilters(filters)
    .withCustomFilters(customFilters)
    .withPriceFilters(priceFilters)
    .withCluster(cluster)
    .withShowAllFavourites(showAllFavourites, includeShowAllFavourites);
  if (isBetaFeatureEnabled('HIDE_OOS_ITEMS')) {
    queryBuilder.withShownOOS(!hiddenOOS);
  } else {
    queryBuilder.withHiddenOOS(hiddenOOS);
  }

  return queryBuilder
    .build();
}

function getCategoryPath(pageParameters) {
  const { category } = pageParameters || {};
  return category && category.length ? `/${slugifyArray(category, '/')}` : '';
}

export function getOfferLink(offer) {
  const { id, text, type } = offer;
  const promoLinkObj = promoLinkObject[type];
  if (typeof promoLinkObj === 'function') {
    return `${promoLinkObj(id, text, type)}`;
  }
  return null;
}

function getLinkWithCategoryAndQuery(pageParameters, basePath, includeShowAllFavourites = false) {
  return new Url(`${basePath}${getCategoryPath(pageParameters)}`)
    .addQueryParameters(getQuery(pageParameters, includeShowAllFavourites)).toString();
}

export function getRegularsLink(pageParameters, basePath, includeShowAllFavourites = false) {
  return getLinkWithCategoryAndQuery(pageParameters, basePath, includeShowAllFavourites);
}

export function getCatalogueLink(pageParameters, basePath, includeShowAllFavourites = false) {
  return getLinkWithCategoryAndQuery(pageParameters, basePath, includeShowAllFavourites);
}

export function getCatalogueLinkWithoutFilters(pageParameters, basePath) {
  // use only the non-result-filtering page params
  const { campaigns, category, searchTerm, sort, hiddenOOS } = pageParameters || {};
  const nonFilteringPageParams = { campaigns, category, searchTerm, sort };
  if (isBetaFeatureEnabled('HIDE_OOS_ITEMS')) {
    nonFilteringPageParams.hiddenOOS = hiddenOOS;
  }
  return getCatalogueLink(nonFilteringPageParams, basePath);
}

export function getSearchLinkWithoutFilters(pageParameters, basePath) {
  // use only the non-result-filtering page params
  const { category, searchTerm, cluster } = pageParameters || {};
  return getCatalogueLink({ category, searchTerm, cluster }, basePath);
}

function getFiltersForCanonicalLink(filters = [], category, searchTerm) {
  if ((category && category.length) || searchTerm) {
    return [];
  }

  // if category or search term is present, use only them to generate canonical link
  // if category is not present, but brand filter is, then use brand filter only for canonical link
  // if brand is not present, but option filter(s) is, then use option filter(s) only for canonical link
  // if option filter is not present, but dietary filter(s) is, then use dietary filter(s) only for canonical link
  // otherwise use custom filters to generate canonical link

  const filterTypesByImportance = [FilterType.BRAND, FilterType.OPTION, FilterType.DIETARY, FilterType.CUSTOM];
  for (const filterType of filterTypesByImportance) {
    const filtersWithGivenType = filters.filter(filter => filter.filterType === filterType);
    if (filtersWithGivenType.length) {
      return filtersWithGivenType;
    }
  }

  return [];
}

export function getCatalogueCanonicalLink(pageParameters, basePath) {
  const { searchTerm, category, filters = [] } = pageParameters || {};
  const filtersForCanonicalLink = getFiltersForCanonicalLink(filters, category, searchTerm);
  const canonicalPageParams = { searchTerm, category, filters: filtersForCanonicalLink };
  if (isBetaFeatureEnabled('HIDE_OOS_ITEMS')) {
    canonicalPageParams.hiddenOOS = true;
  }
  return getCatalogueLink(canonicalPageParams, basePath);
}

export function getOfferDetailsCanonicalLink(pageParameters, basePath) {
  const { id, shortDescription } = pageParameters;
  return `${basePath}/${slugifyParam(shortDescription, id)}`;
}

export function getCatalogueLinkWithoutHideOOSFilter(pageParameters, basePath) {
  return getCatalogueLink({
    ...pageParameters,
    hiddenOOS: false,
  }, basePath);
}

export function getShowAllFavouritesLink(pageParameters) {
  return getCatalogueLink({
    ...pageParameters,
    showAllFavourites: true,
  }, '/search', true);
}
